import Notification from '../../core/Notification';
import VersionWarning from '../../core/VersionWarning';
import { authorisationService } from '../../core/services/security/authorisationService';
import ProjectProvider from '../../core/providers/ProjectProvider';
import { withRouter } from 'react-router-dom';
import PropTypes from 'prop-types';
import { Component, default as React } from 'react';
import Errored from '../../core/Errored';
import Loading from '../../core/Loading';
import { makeCancelable, sortObjects } from '../../core/utils/utils';
import withGS from '../../core/withGShoc';
import GS from '../../grafsync/grafsync';

const loadOptions = {};

class OntologyProjectInfoContainer extends Component {

  constructor(props) {
    super(props);
    this.state = {
      initialLoad: true,
      isLoading: true,
      infoLoading: true,
      ontologies: null,
      projectUserss: undefined,
      isErrored: undefined,
      entitlements: undefined
    };
    this.cancellable = null;
  }

  static getDerivedStateFromProps(props, state) {
    if (props.data._version !== state._version) {
      return {
        infoLoading: true,
        isLoading: false,
        _version: props.data._version,
      };
    }
    return null;
  }

  componentDidUpdate() {
    if (!this.state.isErrored && this.state.infoLoading === true) {
      this.collectProjectData();
    }
  }

  componentDidMount() {
    this.collectProjectData();
  }

  loadData(iris, options) {
    this.cancellable = makeCancelable(GS.loadData(iris, options, this.props.graph, false));
    return this.cancellable;
  }

  loadEntitlements() {
    this.cancellable = makeCancelable(authorisationService.getProjectEntitlements(this.props.user, GS.getActiveSubscription(), GS.getActiveSubscriptionBucket(), this.props.project));
    return this.cancellable;
  }

  componentWillUnmount() {
    if (this.cancellable) this.cancellable.cancel();
  }

  doGetUsers() {
    this.cancellable = makeCancelable(GS.subscriptionUsers(this.props.id));
    return this.cancellable;
  }

  collectProjectData() {
    if (this.state.isLoading) return;
    this.setState({ isLoading: true }, async () => {
      try {
        // We only load these once - they won't change very often at all
        if (this.state.entitlements === undefined) {
          const entitlements = await this.loadEntitlements().promise;
          if (this.cancellable && this.cancellable.isCancelled()) return;
          const users = await this.doGetUsers().promise;
          if (this.cancellable && this.cancellable.isCancelled()) return;
          const projectUsers = users.map(user => ({
            value: user.id,
            label: user.givenName && user.familyName ?
              user.givenName + " " + user.familyName
              :
              user.username
          })).sort(function (a, b) {
            return a.label.toLowerCase() < b.label.toLowerCase() ? -1 : 1;
          });
          this.setState({ entitlements: entitlements.hasEntitlement, projectUsers });
        }
        if (this.props.data.hasComponent) {
          if (this.props.data.hasComponent.length > 0) {
            await this.loadData(this.props.data.hasComponent, loadOptions).promise;
            if (this.cancellable && this.cancellable.isCancelled()) return;
          }
          this.processComponents();
        }
      }
      catch (e) {
        if (this.cancellable && this.cancellable.isCancelled()) return;
        this.setState({ isErrored: e });
      }
      this.setState({ isLoading: false, infoLoading: false, initialLoad: false });
    });
  }

  processComponents() {
    let ontologies = [];
    this.props.data.hasComponent.forEach(component => {
      const componentData = GS.data(component, this.props.graph);
      if (componentData.type.includes("Ontology")) {
        ontologies.push({
          id: componentData.id,
          title: componentData.title,
          description: componentData.description
        });
      }
    });
    sortObjects(ontologies, 'title', this.props.data.defaultLang, "und", false);
    this.setState({ ontologies });
  }

  addComponent = async (title, componentIRI, isSlashIRI, description, lang = "en", isOrdered, isUuidIRI) => {
    let newComponent = {
      id: componentIRI,
      type: "Ontology",
      title: { [lang]: title },
      useSlashIRI: isSlashIRI,
      useUuidIRI: isUuidIRI,
      minimiseEncoding: true,
      hiddenProp: [
        "http://www.w3.org/2000/01/rdf-schema#seeAlso",
        "http://www.w3.org/2004/02/skos/core#definition",
        "http://www.w3.org/2004/02/skos/core#changeNote",
        "http://www.w3.org/2004/02/skos/core#editorialNote",
        "http://www.w3.org/2004/02/skos/core#example",
        "http://www.w3.org/2004/02/skos/core#hiddenNote",
        "http://www.w3.org/2004/02/skos/core#historyNote",
        "http://www.w3.org/2004/02/skos/core#altLabel",
        "http://www.w3.org/2004/02/skos/core#prefLabel",
        "http://www.w3.org/2004/02/skos/core#scopeNote"
      ],
      ...(description && description !== "") && { description: { [lang]: description } }
    };
    await GS.insertResource(newComponent, componentIRI, this.props.graph);
    GS.addProperty(this.props.project, this.props.graph, 'hasComponent', componentIRI);
  };

  deleteComponent = (iri, link) => {
    GS.deleteResource(iri, this.props.graph);
    this.props.history.push(link);
  };

  monitorLinkUpdate = (iri, property) => {
    if (property === "title") this.processComponents();
  };

  render() {

    const { id, data, passthroughComponent, graph, projectType } = this.props;
    const { isLoading, isErrored, ontologies, entitlements, initialLoad, projectUsers } = this.state;

    if (data.isVersionOfProject) return <VersionWarning />;
    if (projectType && projectType !== data.type) return <Notification text="You are trying to view an incorrect project type." />;
    if (isErrored) return <Errored data={isErrored} />;
    if (isLoading && initialLoad) return <Loading />;
    if (!entitlements) return null;

    const WrappedComponent = passthroughComponent;

    return (
      <ProjectProvider value={data}>
        <WrappedComponent
          {...this.props}
          id={id}
          graph={graph}
          project={id}
          data={data}
          projectUsers={projectUsers}
          entitlements={entitlements}
          ontologies={ontologies}
          components={ontologies}
          addComponent={this.addComponent}
          onDeleteComponent={this.deleteComponent}
          monitorLinkUpdate={this.monitorLinkUpdate}
        />
      </ProjectProvider>
    );
  }
}

OntologyProjectInfoContainer.propTypes = {
  data: PropTypes.object.isRequired,
  id: PropTypes.string.isRequired,
  history: PropTypes.object.isRequired,
  project: PropTypes.string.isRequired,
  projectType: PropTypes.string,
  user: PropTypes.object.isRequired,
  graph: PropTypes.string.isRequired
};

export default withRouter(withGS(OntologyProjectInfoContainer));